import React, { useState } from 'react';
import { Building2, TrendingUp, Shield, Users, CheckCircle, ArrowRight, BarChart3, Zap } from 'lucide-react';

const CorporateWellness = () => {
  const [activeProgram, setActiveProgram] = useState(0);

  const programs = [
    {
      title: "On-site Ergonomic Assessments",
      description: "Comprehensive workplace evaluations and workstation modifications to prevent musculoskeletal disorders and boost productivity.",
      features: ["Workstation Setup", "Posture Analysis", "Equipment Recommendations", "Follow-up Support"],
      icon: Building2,
      image: "https://images.pexels.com/photos/7551667/pexels-photo-7551667.jpeg?auto=compress&cs=tinysrgb&w=600"
    },
    {
      title: "Injury-Prevention Workshops",
      description: "Group exercise sessions and injury-prevention workshops for workforce health and engagement.",
      features: ["Posture Training", "Stress Management", "Exercise Programs", "Health Education"],
      icon: Users,
      image: "https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg?auto=compress&cs=tinysrgb&w=600"
    },
    {
      title: "Virtual Physiotherapy & Tele-Wellness Coaching",
      description: "Remote physiotherapy consultations, tele-wellness coaching, and follow-ups for distributed teams.",
      features: ["Virtual Consultations", "Exercise Supervision", "Progress Monitoring", "24/7 Support"],
      icon: Zap,
      image: "https://images.pexels.com/photos/4491459/pexels-photo-4491459.jpeg?auto=compress&cs=tinysrgb&w=600"
    },
    {
      title: "Data-driven ROI Reporting",
      description: "Detailed reporting on reduced absenteeism, injury rates, and improved employee wellness for your organization.",
      features: ["Absenteeism Tracking", "Injury Rate Analysis", "Employee Wellness Metrics", "Custom Reports"],
      icon: BarChart3,
      image: "https://images.pexels.com/photos/4386467/pexels-photo-4386467.jpeg?auto=compress&cs=tinysrgb&w=600"
    }
  ];

  const benefits = [
    { icon: TrendingUp, title: "40% Reduction", subtitle: "in workplace injuries" },
    { icon: BarChart3, title: "35% Decrease", subtitle: "in absenteeism rates" },
    { icon: Users, title: "90% Increase", subtitle: "in employee satisfaction" },
    { icon: Shield, title: "50% Lower", subtitle: "insurance claims" }
  ];

  const testimonials = [
    {
      company: "TechCorp Indore",
      logo: "https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg?auto=compress&cs=tinysrgb&w=100",
      quote: "Arpan PhysioCure™'s corporate wellness program transformed our workplace. Employee satisfaction increased by 40% and sick days decreased significantly.",

      author: "Rajesh Sharma",
      position: "HR Director"
    },
    {
      company: "IndusManufacturing",
      logo: "https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg?auto=compress&cs=tinysrgb&w=100",
      quote: "The ergonomic assessments and on-site clinics have been game-changers for our manufacturing facility. Highly recommend their services.",
      author: "Priya Patel",
      position: "Operations Manager"
    }
  ];

  return (
    <section id="corporate" className="pt-32 sm:pt-36 pb-16 bg-gradient-to-b from-gray-50 to-white scroll-mt-36">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Corporate Wellness Programs
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto mb-8 px-4">
            Transform your workplace health with our comprehensive wellness solutions. 
            Reduce costs, improve productivity, and create a healthier work environment.
          </p>
          <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 max-w-4xl mx-auto">
            <div className="flex items-center justify-center mb-4">
              <div className="bg-emerald-100 p-3 rounded-full mr-4">
                <TrendingUp className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900">Proven Results</h3>
            </div>
            <p className="text-base sm:text-lg text-gray-600">
              Our corporate clients see an average ROI of 300% within the first year through 
              reduced healthcare costs and improved employee productivity.
            </p>
          </div>
        </div>

        {/* Benefits Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-8 mb-16">
          {benefits.map((benefit, index) => (
            <div key={index} className="text-center group">
              <div className="bg-white shadow-lg rounded-xl p-4 sm:p-6 hover:shadow-xl transition-all duration-300 group-hover:scale-105">
                <div className="bg-sky-100 w-12 sm:w-16 h-12 sm:h-16 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-sky-200 transition-colors duration-300">
                  <benefit.icon className="w-8 h-8 text-sky-600" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">{benefit.title}</h3>
                <p className="text-gray-600 text-sm">{benefit.subtitle}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Programs Section */}
        <div className="mb-16">
          <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center mb-8 sm:mb-12">Our Wellness Programs</h3>
          
          {/* Program Tabs */}
          <div className="flex flex-wrap justify-center mb-8 bg-white rounded-2xl sm:rounded-full p-2 shadow-lg max-w-4xl mx-auto">
            {programs.map((program, index) => (
              <button
                key={index}
                onClick={() => setActiveProgram(index)}
                className={`flex items-center px-3 sm:px-6 py-2 sm:py-3 rounded-full font-semibold transition-all duration-300 text-sm sm:text-base ${
                  activeProgram === index 
                    ? 'bg-sky-600 text-white shadow-lg' 
                    : 'text-gray-600 hover:text-sky-600'
                }`}
              >
                <program.icon className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">{program.title}</span>
                <span className="sm:hidden">{program.title.split(' ')[0]}</span>
              </button>
            ))}
          </div>

          {/* Program Content */}
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-2">
              <div className="p-6 sm:p-8 lg:p-12 order-2 lg:order-1">
               {(() => {
                 const ProgramIcon = programs[activeProgram].icon;
                 return (
                <div className="flex items-center mb-6">
                  <div className="bg-sky-600 p-3 rounded-full mr-4">
                   <ProgramIcon className="w-6 h-6 text-white" />
                  </div>
                  <h4 className="text-2xl font-bold text-gray-900">
                    {programs[activeProgram].title}
                  </h4>
                </div>
                 );
               })()}
                <p className="text-base sm:text-lg text-gray-600 mb-6 sm:mb-8">
                  {programs[activeProgram].description}
                </p>
                <div className="space-y-4 mb-8">
                  <h5 className="text-lg font-semibold text-gray-900">Program Features:</h5>
                  {programs[activeProgram].features.map((feature, index) => (
                    <div key={index} className="flex items-center">
                      <CheckCircle className="w-5 h-5 text-emerald-500 mr-3" />
                      <span className="text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
                <button className="bg-emerald-500 hover:bg-emerald-600 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full font-semibold transition-all duration-200 transform hover:scale-105 flex items-center w-full sm:w-auto justify-center">
                  Get Custom Proposal
                  <ArrowRight className="ml-2 w-5 h-5" />
                </button>
              </div>
              <div className="relative order-1 lg:order-2 h-64 sm:h-80 lg:h-auto">
                <img
                  src={programs[activeProgram].image}
                  alt={programs[activeProgram].title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
              </div>
            </div>
          </div>
        </div>

        {/* Testimonials */}
        <div className="mb-16">
          <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center mb-8 sm:mb-12">What Our Corporate Clients Say</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-6 sm:p-8 hover:shadow-xl transition-all duration-300">
                <div className="flex items-center mb-6">
                  <img
                    src={testimonial.logo}
                    alt={testimonial.company}
                    className="w-12 h-12 rounded-full object-cover mr-4"
                  />
                  <div>
                    <h4 className="font-bold text-gray-900">{testimonial.company}</h4>
                    <p className="text-gray-600 text-sm">Corporate Partner</p>
                  </div>
                </div>
                <p className="text-sm sm:text-base text-gray-700 mb-6 italic">"{testimonial.quote}"</p>
                <div className="border-t pt-4">
                  <p className="font-semibold text-gray-900">{testimonial.author}</p>
                  <p className="text-gray-600 text-sm">{testimonial.position}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-sky-600 to-emerald-600 rounded-2xl p-6 sm:p-8 md:p-12 text-center">
          <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4">Ready to Transform Your Workplace?</h3>
          <p className="text-lg sm:text-xl text-sky-100 mb-6 sm:mb-8 max-w-2xl mx-auto">
            Let's discuss how our corporate wellness programs can improve your employee health 
            and reduce your healthcare costs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-white text-sky-600 hover:bg-gray-100 px-6 sm:px-8 py-3 sm:py-4 rounded-full font-semibold transition-all duration-200 transform hover:scale-105">
              Schedule Consultation
            </button>
            <button className="border-2 border-white text-white hover:bg-white hover:text-sky-600 px-6 sm:px-8 py-3 sm:py-4 rounded-full font-semibold transition-all duration-200">
              Download Brochure
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CorporateWellness;